import React, { useMemo, useState } from 'react';
import CustomModal from 'shared/components/andtComponents/Modal';
import { cloneDeep } from 'lodash';
import toast from 'shared/components/andtComponents/Toast';
import { useMspCustomers } from 'users/new-user-management/hooks/reactQuery/useMspCustomers.js';
import Input from 'shared/components/andtComponents/Input';
import {
  CUSTOMER_ADD_REMOVE_LINKED_ACCOUNTS_COLUMNS,
  CUSTOMER_ADD_REMOVE_PAYER_ACCOUNTS_COLUMNS,
} from 'users/containers/Organization/consts.js';
import AccountsComponentWrapper from 'users/containers/Organization/components/LinkedAccounts/AccountsComponentWrapper.jsx';
import {
  provideAccountsData,
} from 'users/containers/Organization/components/LinkedAccounts/dataAccessHelperFunctions.js';
import { useAccounts } from 'users/new-user-management/hooks/reactQuery/useAccounts';
import Spinner from 'shared/components/andtComponents/Spinner.jsx';
import { isLinkedAccount } from 'users/containers/Organization/Customers/customersSharedHelpers';
import { isEmailValid } from 'shared/utils/strUtil';

import styles from './EditCustomerModal.module.scss';
import { ICustomersTableRow, ICustomerLinkedAccount} from '../types/customers';

interface IActivateCustomerModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  customer: ICustomersTableRow
}


interface ReactQueryResponse {
  isLoading: boolean;
  data: unknown;
}

interface IFetchCustomerAccountsResponse extends ReactQueryResponse {
  data: {
    fullyDedicatedPayerAccounts: Array<{ payerAccountId: string }>;
    linked_accounts: ICustomerLinkedAccount[];
  }
}

interface IFetchAvailableLinkedAccountsResponse extends ReactQueryResponse {
  data: { payerAccountId: string, linkedAccountIds: string[] }[]
}

const ActivateCustomerModal = ({ isOpen, setIsOpen, customer }: IActivateCustomerModalProps) => {
  const [adminEmail, setAdminEmail] = useState('');
  const { activateCustomer } = useMspCustomers();
  const { fetchDataAccessPayerAccounts } = useAccounts(!isOpen);
  const { fetchCustomerAccounts, fetchAvailableLinkedAccounts } = useMspCustomers();
  const { data: accountsData, isLoading } = fetchDataAccessPayerAccounts();
  const { data: accounts, isLoading: customerLoading } = fetchCustomerAccounts(customer) as IFetchCustomerAccountsResponse;
  const { data: availableLinkedAccounts, isLoading: availableLinkedAccountsLoading } = fetchAvailableLinkedAccounts() as IFetchAvailableLinkedAccountsResponse;


  const accountsToDisplay = useMemo(() => {
    if (!accountsData || availableLinkedAccountsLoading || customerLoading) {
      return null;
    }
    const accountsDataCopy = cloneDeep(accountsData);
    const combinedPayerAccountsData = provideAccountsData({
      accountsData: accountsDataCopy,
      roleDataAccess: [],
      currentRoleId: null,
      isCreateEditMode: true,
      entity: 'customer',
    });
    combinedPayerAccountsData.forEach((payer) => {
      const availableLns = (
        availableLinkedAccounts.find((availableLinkedAccount) => availableLinkedAccount.payerAccountId === payer.payerAccount.id)?.linkedAccountIds || []
      ).filter(Boolean);
      const dedicated = accounts?.fullyDedicatedPayerAccounts?.some((a) => a.payerAccountId === payer.payerAccount.id);
      if (dedicated) {
        payer.isSelectedInitial = true;
      }
      payer.payerAccount.isNotAvailable = true;
      payer.linkedAccounts.forEach((ln) => {
        const linkedAccount = isLinkedAccount({ linkedAccounts: accounts?.linked_accounts, payerAccountId: payer.payerAccount.id, linkedAccountId: ln.id });
        if (!availableLns.includes(ln.id) && !linkedAccount) {
          ln.isNotAvailable = true;
        }
        if (linkedAccount) {
          ln.isSelectedInitial = true;
        }
      });
    });
    return { accountsData: combinedPayerAccountsData };
  }, [
    accountsData,
    availableLinkedAccountsLoading,
    customerLoading,
    availableLinkedAccounts,
    accounts?.fullyDedicatedPayerAccounts,
    accounts?.linked_accounts,
  ]);

  const onActivate = async () => {
    if (!adminEmail) {
      toast.error('Admin email is required');
      return;
    }
    if (!isEmailValid(adminEmail)) {
      toast.error('Admin email format is invalid');
      return;
    }
    await activateCustomer.mutateAsync({ customerId: customer.id, adminEmail });
    setIsOpen(false);
  };

  return (
    <CustomModal
      className={styles}
      onClose={() => {
        setIsOpen(false);
      }}
      headerMode="edit"
      onSave={onActivate}
      overrideStyles={{ width: 1200 }}
      open={isOpen}
      automationid="activate-customer-modal"
      saveDisabled={activateCustomer.isPending}
      saveTitle="Activate"
      title="Activate Customer"
    >
      {isLoading || customerLoading || availableLinkedAccountsLoading ? (
        <div style={{ height: 400 }}>
          <Spinner />
        </div>
      ) : (
        <div className={styles.container}>
          <div className={styles.properties}>
            <div className={styles.propertiesLine}>
              <div className={styles.propertyContainer}>
                <span className={styles.label}>Customer Name</span>
                <Input
                  disabled
                  value={customer?.name}
                  onChange={() => {}}
                />
              </div>
              <div className={styles.propertyContainer}>
                <span className={styles.label}>Customer Code</span>
                <Input
                  disabled
                  value={customer?.code || undefined}
                  onChange={() => {}}
                />
              </div>
                <div className={styles.propertyContainer}>
                  <span className={styles.label}>Admin Email *</span>
                  <Input
                    value={adminEmail}
                    placeholder="Enter Email"
                    onChange={(e) => {
                      setAdminEmail(e?.target?.value);
                    }}
                  />
                </div>
            </div>
            <div className={styles.propertiesLine}>
              <div className={styles.propertyContainer}>
                <span className={styles.label}>Description</span>
                <Input
                  disabled
                  className={styles.description}
                  value={customer?.description || ''}
                  onChange={() => {}}
                />
              </div>
            </div>
          </div>
          <AccountsComponentWrapper
            additionalData={{ customerId: customer?.id }}
            allAccountsData={accountsToDisplay}
            entity="customer"
            isCreateEditMode
            onSelectionChange={() => {}}
            linkedAccountsColumns={[
              CUSTOMER_ADD_REMOVE_LINKED_ACCOUNTS_COLUMNS.LINKED_ACCOUNT.columnName,
              CUSTOMER_ADD_REMOVE_LINKED_ACCOUNTS_COLUMNS.ASSIGNED_BY.columnName,
            ]}
            isViewOnly
            payerAccountsColumns={[CUSTOMER_ADD_REMOVE_PAYER_ACCOUNTS_COLUMNS.PAYER_ACCOUNT.columnName]}
          />
        </div>
      )}
    </CustomModal>
  );
};

export default ActivateCustomerModal;
